import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import Overlay, { OverlaySection } from '../components/Overlay'
import { useDispatch, useSelector } from '../store/hooks'
import TableList, { ListItemProps } from '../components/TableList'
import { useCurrentApplication } from '../store/hooks/uiHooks'
import { getSystemHeight, useCurrentConfiguration } from '../store/hooks/configurationHooks'
import FinalForm from './FinalForm'
import { isServiceData, ServiceData } from '../../types/ModuleData'
import { useAvailableModules, useTranslation } from '../store/hooks/dataHooks'
import { isDefined } from '../utils/typeGuards'
import { isMenuMultiProperty } from '../../types/PropertyData'
import numeral from 'numeral'
import { getNumberedModuleTitles } from '../model/ConstelaConfiguration'
import Switch from '../components/Switch'
import { ConstelaConfiguration, ConstelaConfigurationModule } from '../../types/ConstelaConfiguration'
import PreviewLuminary from '../components/preview/PreviewLuminary'
import { useResizeDetector } from 'react-resize-detector'

const Section = styled.div`
  margin-top: 100px;
`

const OptionalSidebar = styled<React.FC<{ sidebar?: JSX.Element; className?: string }>>(function OptionalSidebar({
  sidebar,
  className,
  children
}) {
  if (sidebar) {
    return (
      <div className={`row ${className}`}>
        <div className="col-md-8">{children}</div>
        <div className="col-md-4">{sidebar}</div>
      </div>
    )
  }
  return <>{children}</>
})`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  > * {
    float: none;
    display: flex;
    flex-direction: column;
  }
`

const PreviewWrap = styled<
  React.FC<{
    className?: string
    children: (scale: number) => JSX.Element
  }>
>(function PreviewWrap({ className, children }) {
  const { width, ref } = useResizeDetector()
  const widthmm = useSelector((state) => state.data.system.width)

  return (
    <div className={className}>
      <div ref={ref}>{children?.((width ?? 0) / widthmm)}</div>
    </div>
  )
})`
  flex-grow: 1;
  background-color: #f2f2f2;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;

  > div {
    width: 100%;
    max-width: 250px;
  }
`

type ServiceTileProps = {
  service: ServiceData
  configuration: ConstelaConfiguration
  setServices: (nextServices: ConstelaConfigurationModule[]) => unknown
  className?: string
}
const ServiceTile = styled<React.FC<ServiceTileProps>>(function ServiceTile({
  service,
  configuration,
  setServices,
  className
}) {
  const selected = !!configuration.services.find((x) => x.id === service.id)
  const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setServices?.(
        configuration.services.filter((x) => x.id !== service.id).concat(e.target.checked ? [{ id: service.id }] : [])
      )
    },
    [configuration.services, service.id, setServices]
  )

  return (
    <div className={`col-xs-12 col-md-6 col-lg-4 ${className}`} key={service.id}>
      <Switch checked={selected} onChange={onChange}>
        {service.title}
      </Switch>
      <p>{service.description}</p>
    </div>
  )
})`
  ${Switch} {
    display: flex;
    margin-top: 10px;
    margin-bottom: 10px;
  }
`

const FinalView: React.FC = () => {
  const showFinal = useSelector((state) => {
    return state.ui.showFinal
  })
  const t = useTranslation()
  const application = useCurrentApplication()
  const configuration = useCurrentConfiguration()
  const dispatch = useDispatch()
  const setServices = useCallback(
    (nextServices: ConstelaConfigurationModule[]) => {
      dispatch({
        type: 'Configuration/SetServices',
        mode: 'expert',
        services: nextServices
      })
    },
    [dispatch]
  )
  const modules = useSelector((state) => {
    return state.data.modules
  })

  const isExpert = useSelector((state) => state.ui.mode === 'expert')

  const configItems: ListItemProps[] = useMemo(() => {
    const moduleTitles = getNumberedModuleTitles(configuration.modules, modules, configuration.type === 'inspiration')

    const result: (ListItemProps | false)[] = [
      {
        label: t('final.application'),
        value: application?.title
      },
      {
        label: t('final.modules'),
        value: moduleTitles.join(', ')
      },
      !isExpert && {
        label: t('final.services'),
        value: configuration.services
          .map(({ id }) => modules.find((module) => module.id === id))
          .filter(isServiceData)
          .map((x) => x.title)
          .join(', ')
      },
      {
        label: t('ui.totalheight'),
        value: numeral(getSystemHeight(modules, configuration) / 1000).format('0.[00]') + ' m'
      }
    ]
    return result.filter((x): x is ListItemProps => !!x && !!x.value)
  }, [application?.title, configuration, isExpert, modules, t])

  const properties = useSelector((state) => state.data.properties)
  const propertyMenu = useSelector((state) => state.data.menu)
  const propertyLists = useMemo(() => {
    if (!isExpert) {
      return []
    }
    const result = propertyMenu
      .map(({ displayName, contents, id }): { title: string; items: ListItemProps[]; id: string } => {
        return {
          id,
          title: displayName,
          items: contents
            .map(
              (item): ListItemProps => {
                if (isMenuMultiProperty(item)) {
                  const selectedProperties = item.properties
                    .filter((id) => configuration.properties[id] === 'true')
                    .map((id) => properties.find((x) => x.id === id))
                    .filter(isDefined)
                  return {
                    label: item.displayName,
                    value: selectedProperties.length ? selectedProperties.map((x) => x.displayName).join(', ') : '–'
                  }
                } else {
                  const property = properties.find((x) => x.id === item.property)
                  const value = property?.values.find((x) => x.id === configuration.properties[item.property])
                  return {
                    label: property?.displayName,
                    value: value?.displayName
                  }
                }
              }
            )
            .filter((x) => x.label && x.value)
        }
      })
      .filter((x) => x.items.length)

    // Add properties for configured special modules
    const configuredModules = configuration.modules.filter((x) => Object.keys(x.properties ?? {}).length > 0)
    const configuredModuleTitles = getNumberedModuleTitles(configuredModules, modules)
    configuredModules.forEach((module, i) => {
      result.push({
        id: `configured-module-${i}`,
        title: configuredModuleTitles[i],
        items: Object.entries(module.properties ?? {}).map(([key, value]) => ({
          label: properties.find((x) => x.id === key)?.displayName ?? key,
          value: properties.find((x) => x.id === key)?.values.find((x) => x.id === value)?.displayName ?? value
        }))
      })
    })

    return result
  }, [configuration.modules, configuration.properties, isExpert, modules, properties, propertyMenu])

  const availableServices = useSelector((state) =>
    state.data.modules
      .filter(isServiceData)
      .filter((x) => !Object.keys(x.equivalentProperty ?? {}).length && !x.expertHidden)
  )

  const availableModules = useAvailableModules(application?.id ?? '', configuration.type)

  return (
    <Overlay visible={showFinal}>
      <OverlaySection $title={t('final.headline')} $subline="Constela 19 LED">
        <Section>
          <OptionalSidebar
            sidebar={
              configuration.type === 'expert' ? (
                <PreviewWrap>
                  {(scale) => (
                    <PreviewLuminary configuration={configuration} availableModules={availableModules} scale={scale} />
                  )}
                </PreviewWrap>
              ) : undefined
            }
          >
            <h3>{t('final.headline')}</h3>
            <TableList items={configItems} />
            {propertyLists.map(({ title, id, items }) => (
              <React.Fragment key={id}>
                <h3>{title}</h3>
                <TableList items={items} />
              </React.Fragment>
            ))}
          </OptionalSidebar>
        </Section>
        {configuration.type === 'expert' && (
          <Section>
            <h3>{t('final.services')}</h3>
            <div className="row">
              {availableServices.map((service) => (
                <ServiceTile
                  key={service.id}
                  service={service}
                  configuration={configuration}
                  setServices={setServices}
                />
              ))}
            </div>
          </Section>
        )}
        <Section>
          <h3>{t('ui.request-offer')}</h3>
          <p>{t('final.text')}</p>
          <FinalForm />
        </Section>
      </OverlaySection>
    </Overlay>
  )
}

export default FinalView
